// Account client — register / sign in / sign out against /api/auth/*,
// with the session held in localStorage. The account is browser-global
// (deliberately NOT per-local-profile): it is the player's owned
// identity for the leaderboard, while local profiles remain the
// device-side save containers.

const STORE_KEY = 'settle:account';

export interface StoredAccount { token: string; username: string; over13?: boolean }
export interface AuthResult { ok: boolean; error?: string; username?: string }

let cached: StoredAccount | null | undefined;

export function getAccount(): StoredAccount | null {
  if (cached !== undefined) return cached;
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) as StoredAccount : null;
    cached = parsed && typeof parsed.token === 'string' && typeof parsed.username === 'string' ? parsed : null;
  } catch {
    cached = null;
  }
  return cached;
}

function setAccount(acct: StoredAccount | null): void {
  cached = acct;
  try {
    if (acct) localStorage.setItem(STORE_KEY, JSON.stringify(acct));
    else localStorage.removeItem(STORE_KEY);
  } catch { /* private mode */ }
}

/** Authorization header for API calls; empty object when signed out. */
export function authHeader(): Record<string, string> {
  const acct = getAccount();
  return acct ? { authorization: `Bearer ${acct.token}` } : {};
}

async function post(path: string, body: unknown, withAuth = false): Promise<Record<string, unknown>> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(withAuth ? authHeader() : {}) },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null) as Record<string, unknown> | null;
  if (!data) return { ok: false, error: `http ${res.status}` };
  return data;
}

export async function register(username: string, password: string, email: string, over13: boolean): Promise<AuthResult> {
  try {
    const data = await post('/api/auth/register', { username, password, email: email || undefined, over13 });
    if (data.ok && typeof data.token === 'string' && typeof data.username === 'string') {
      setAccount({ token: data.token, username: data.username, over13: data.over13 !== false });
      return { ok: true, username: data.username };
    }
    return { ok: false, error: typeof data.error === 'string' ? data.error : 'registration failed' };
  } catch {
    return { ok: false, error: 'network error' };
  }
}

export async function login(username: string, password: string): Promise<AuthResult> {
  try {
    const data = await post('/api/auth/login', { username, password });
    if (data.ok && typeof data.token === 'string' && typeof data.username === 'string') {
      setAccount({ token: data.token, username: data.username, over13: data.over13 !== false });
      return { ok: true, username: data.username };
    }
    return { ok: false, error: typeof data.error === 'string' ? data.error : 'sign in failed' };
  } catch {
    return { ok: false, error: 'network error' };
  }
}

export async function logout(): Promise<void> {
  try {
    await post('/api/auth/logout', {}, true);
  } catch { /* token is being discarded regardless */ }
  setAccount(null);
}

export async function requestReset(username: string): Promise<AuthResult> {
  try {
    const data = await post('/api/auth/request-reset', { username });
    return data.ok ? { ok: true } : { ok: false, error: typeof data.error === 'string' ? data.error : 'request failed' };
  } catch {
    return { ok: false, error: 'network error' };
  }
}

export async function resetPassword(username: string, code: string, password: string): Promise<AuthResult> {
  try {
    const data = await post('/api/auth/reset', { username, code, password });
    if (data.ok && typeof data.token === 'string' && typeof data.username === 'string') {
      setAccount({ token: data.token, username: data.username, over13: data.over13 !== false });
      return { ok: true, username: data.username };
    }
    return { ok: false, error: typeof data.error === 'string' ? data.error : 'reset failed' };
  } catch {
    return { ok: false, error: 'network error' };
  }
}

/** Validate the stored session at boot — clears it if the server says
 *  it's dead so the UI never claims a sign-in it can't use. */
export async function validateSession(): Promise<void> {
  const acct = getAccount();
  if (!acct) return;
  try {
    const res = await fetch('/api/auth/me', { headers: authHeader() });
    if (res.status === 401) { setAccount(null); return; }
    if (res.ok) {
      const data = await res.json() as { ok?: boolean; over13?: boolean };
      // Refresh the age flag from the server — chat gating depends on it.
      if (data.ok) setAccount({ ...acct, over13: data.over13 !== false });
    }
    // Network errors / 503 keep the stored session — could be vite
    // dev or a transient outage; the next submit will surface it.
  } catch { /* offline — keep */ }
}
